/**
 * Workflow Configuration
 * 
 * This file defines the complete workflow logic, permissions, and business rules
 * based on the pseudocode workflow requirements.
 */

import { InvoiceStatus, InvoiceClassification, InvoiceField } from '@config/invoice-config';

// Constants for mandatory assignment statuses (single source of truth)
export const MANDATORY_ASSIGNMENT_STATUSES = [
  InvoiceStatus.ADDING,
  InvoiceStatus.IR,
  InvoiceStatus.INVOICING,
  InvoiceStatus.ENTERING_STOCK
] as const;

// User Roles
export enum UserRole {
  ADMINISTRATOR = 'Administrator',
  FINANCE = 'Finance',
  STOCK = 'Stock'
}

// Permission Levels
export enum PermissionLevel {
  HIDDEN = 'hidden',
  VIEW = 'view',
  EDIT = 'edit',
  REQUIRED = 'required'
}

// Actions that can be performed
export enum WorkflowAction {
  ASSIGN = 'assign',
  MOVE_TO_NEXT = 'move_to_next',
  UPDATE_NOTES = 'update_notes',
  VIEW = 'view'
}

// Status-specific business rules
export const STATUS_BUSINESS_RULES = {
  [InvoiceStatus.UNPROCESSED]: {
    allowedRoles: [UserRole.FINANCE, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: true,
    nextStatusOptions: [
      { status: InvoiceStatus.ADDING, condition: 'classification === "deliver_direct"' },
      { status: InvoiceStatus.HEAD_OFFICE_STOCK, condition: 'classification === "head_office"' },
      { status: InvoiceStatus.EXPENSE_INVOICES, condition: 'classification === "expense"' },
      { status: InvoiceStatus.CREDIT_NOTE, condition: 'classification === "credit_note"' }
    ]
  },
  [InvoiceStatus.ADDING]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: true,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.NEW_PRODUCTS, condition: 'new_stock === true' },
      { status: InvoiceStatus.IR, condition: 'new_stock === false' }
    ]
  },
  [InvoiceStatus.NEW_PRODUCTS]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.ADDING, condition: 'product_added === true' }
    ]
  },
  [InvoiceStatus.IR]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: true,
    canChangeClassification: false,
    separationOfDuties: true,
    previousStepRestriction: InvoiceStatus.ADDING,
    nextStatusOptions: [
      { status: InvoiceStatus.INVOICING, condition: 'ir field is filled' }
    ]
  },
  [InvoiceStatus.INVOICING]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: true,
    canChangeClassification: false,
    separationOfDuties: true,
    previousStepRestriction: InvoiceStatus.IR,
    nextStatusOptions: [
      { status: InvoiceStatus.ENTERING_STOCK, condition: 'inv field is filled' }
    ]
  },
  [InvoiceStatus.HEAD_OFFICE_STOCK]: {
    allowedRoles: [UserRole.FINANCE, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.ENTERING_STOCK, condition: 'all required fields filled' }
    ]
  },
  [InvoiceStatus.EXPENSE_INVOICES]: {
    allowedRoles: [UserRole.FINANCE, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.ENTERING_EXPENSE, condition: 'all required fields filled' }
    ]
  },
  [InvoiceStatus.ENTERING_STOCK]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: true,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.COMPLETED, condition: 'all required fields filled' }
    ]
  },
  [InvoiceStatus.ENTERING_EXPENSE]: {
    allowedRoles: [UserRole.FINANCE, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.COMPLETED, condition: 'all required fields filled' }
    ]
  },
  [InvoiceStatus.CREDIT_NOTE]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.CREDIT_ENTERING, condition: 'claim, cn, and credit_amount fields filled' }
    ]
  },
  [InvoiceStatus.CREDIT_ENTERING]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: false,
    nextStatusOptions: [
      { status: InvoiceStatus.COMPLETED, condition: 'all required fields filled' }
    ]
  },
  [InvoiceStatus.COMPLETED]: {
    allowedRoles: [UserRole.FINANCE, UserRole.STOCK, UserRole.ADMINISTRATOR],
    requiresAssignment: false,
    canChangeClassification: false,
    readOnly: true,
    nextStatusOptions: []
  }
} as const;

// Field permissions by status and role
export const FIELD_PERMISSIONS = {
  [InvoiceStatus.UNPROCESSED]: {
    [UserRole.ADMINISTRATOR]: {
      [InvoiceField.SUPPLIER]: PermissionLevel.EDIT,
      [InvoiceField.INVOICE_NUMBER]: PermissionLevel.EDIT,
      [InvoiceField.INVOICE_DATE]: PermissionLevel.EDIT,
      [InvoiceField.DUE_DATE]: PermissionLevel.EDIT,
      [InvoiceField.TOTAL_AMOUNT]: PermissionLevel.EDIT,
      [InvoiceField.SUPPLIER_ABN]: PermissionLevel.EDIT,
      [InvoiceField.CLASSIFICATION]: PermissionLevel.EDIT,
      [InvoiceField.NOTES]: PermissionLevel.EDIT
    },
    [UserRole.FINANCE]: {
      [InvoiceField.SUPPLIER]: PermissionLevel.REQUIRED,
      [InvoiceField.INVOICE_NUMBER]: PermissionLevel.REQUIRED,
      [InvoiceField.INVOICE_DATE]: PermissionLevel.REQUIRED,
      [InvoiceField.DUE_DATE]: PermissionLevel.REQUIRED,
      [InvoiceField.TOTAL_AMOUNT]: PermissionLevel.REQUIRED,
      [InvoiceField.SUPPLIER_ABN]: PermissionLevel.REQUIRED,
      [InvoiceField.CLASSIFICATION]: PermissionLevel.EDIT,
      [InvoiceField.NOTES]: PermissionLevel.EDIT
    },
    [UserRole.STOCK]: {
      [InvoiceField.SUPPLIER]: PermissionLevel.HIDDEN,
      [InvoiceField.INVOICE_NUMBER]: PermissionLevel.HIDDEN,
      [InvoiceField.INVOICE_DATE]: PermissionLevel.HIDDEN,
      [InvoiceField.DUE_DATE]: PermissionLevel.HIDDEN,
      [InvoiceField.TOTAL_AMOUNT]: PermissionLevel.HIDDEN,
      [InvoiceField.SUPPLIER_ABN]: PermissionLevel.HIDDEN,
      [InvoiceField.CLASSIFICATION]: PermissionLevel.HIDDEN,
      [InvoiceField.NOTES]: PermissionLevel.HIDDEN
    }
  },
  [InvoiceStatus.ADDING]: {
    [UserRole.ADMINISTRATOR]: {
      [InvoiceField.SUPPLIER]: PermissionLevel.VIEW,
      [InvoiceField.INVOICE_NUMBER]: PermissionLevel.VIEW,
      [InvoiceField.INVOICE_DATE]: PermissionLevel.VIEW,
      [InvoiceField.DUE_DATE]: PermissionLevel.VIEW,
      [InvoiceField.TOTAL_AMOUNT]: PermissionLevel.VIEW,
      [InvoiceField.SUPPLIER_ABN]: PermissionLevel.VIEW,
      [InvoiceField.STORE_ID]: PermissionLevel.REQUIRED,
      [InvoiceField.PO]: PermissionLevel.REQUIRED,
      [InvoiceField.SO]: PermissionLevel.REQUIRED,
      [InvoiceField.NEW_STOCK]: PermissionLevel.EDIT,
      [InvoiceField.NOTES]: PermissionLevel.EDIT
    },
    [UserRole.FINANCE]: {
      [InvoiceField.SUPPLIER]: PermissionLevel.VIEW,
      [InvoiceField.INVOICE_NUMBER]: PermissionLevel.VIEW,
      [InvoiceField.INVOICE_DATE]: PermissionLevel.VIEW,
      [InvoiceField.DUE_DATE]: PermissionLevel.VIEW,
      [InvoiceField.TOTAL_AMOUNT]: PermissionLevel.VIEW,
      [InvoiceField.SUPPLIER_ABN]: PermissionLevel.VIEW,
      [InvoiceField.STORE_ID]: PermissionLevel.VIEW,
      [InvoiceField.PO]: PermissionLevel.VIEW,
      [InvoiceField.SO]: PermissionLevel.VIEW,
      [InvoiceField.NEW_STOCK]: PermissionLevel.VIEW,
      [InvoiceField.NOTES]: PermissionLevel.EDIT
    },
    [UserRole.STOCK]: {
      [InvoiceField.SUPPLIER]: PermissionLevel.VIEW,
      [InvoiceField.INVOICE_NUMBER]: PermissionLevel.VIEW,
      [InvoiceField.INVOICE_DATE]: PermissionLevel.VIEW,
      [InvoiceField.DUE_DATE]: PermissionLevel.VIEW,
      [InvoiceField.TOTAL_AMOUNT]: PermissionLevel.VIEW,
      [InvoiceField.SUPPLIER_ABN]: PermissionLevel.VIEW,
      [InvoiceField.STORE_ID]: PermissionLevel.REQUIRED,
      [InvoiceField.PO]: PermissionLevel.REQUIRED,
      [InvoiceField.SO]: PermissionLevel.REQUIRED,
      [InvoiceField.NEW_STOCK]: PermissionLevel.EDIT,
      [InvoiceField.NOTES]: PermissionLevel.EDIT
    }
  }
} as const;

// Separation of Duties Rules
export const SEPARATION_OF_DUTIES = {
  [InvoiceStatus.IR]: {
    previousStep: InvoiceStatus.ADDING,
    restriction: 'User who processed Step 2 cannot process Step 4'
  },
  [InvoiceStatus.INVOICING]: {
    previousStep: InvoiceStatus.IR,
    restriction: 'User who processed Step 4 cannot process Step 5'
  }
} as const;

// Assignment Requirements
export const ASSIGNMENT_REQUIRED_STATUSES = [
  InvoiceStatus.ADDING,
  InvoiceStatus.IR,
  InvoiceStatus.INVOICING,
  InvoiceStatus.ENTERING_STOCK
] as const;

// Helper Functions
export function canUserAccessStatus(userRole: UserRole, status: InvoiceStatus): boolean {
  const rules = STATUS_BUSINESS_RULES[status];
  return rules?.allowedRoles.some((role) => role === userRole) || false;
}

export function isAssignmentRequired(status: InvoiceStatus): boolean {
  return ASSIGNMENT_REQUIRED_STATUSES.some((requiredStatus) => requiredStatus === status);
}

export function hasSeparationOfDutiesRestriction(status: InvoiceStatus): boolean {
  return status in SEPARATION_OF_DUTIES;
}

export function getPreviousStepRestriction(status: InvoiceStatus): InvoiceStatus | null {
  const restriction = status in SEPARATION_OF_DUTIES ? SEPARATION_OF_DUTIES[status as keyof typeof SEPARATION_OF_DUTIES] : null;
  return restriction?.previousStep || null;
}

export function canUserPerformAction(
  userRole: UserRole, 
  status: InvoiceStatus, 
  action: WorkflowAction
): boolean {
  const rules = STATUS_BUSINESS_RULES[status];
  
  if (!rules) return false;
  
  // Check if user role is allowed for this status
  if (!rules.allowedRoles.some((role) => role === userRole)) return false;
  
  // Special action checks
  switch (action) {
    case WorkflowAction.ASSIGN:
      return rules.requiresAssignment;
    case WorkflowAction.MOVE_TO_NEXT:
      return true; // Allowed users can move to next step
    case WorkflowAction.UPDATE_NOTES:
      return true; // Notes are always editable
    case WorkflowAction.VIEW:
      return true; // Allowed users can view
    default:
      return false;
  }
}

export function getNextStatusOptions(
  currentStatus: InvoiceStatus, 
  classification: InvoiceClassification,
  fieldValues?: Record<string, any>
): InvoiceStatus[] {
  const rules = STATUS_BUSINESS_RULES[currentStatus];
  if (!rules?.nextStatusOptions) return [];
  
  return rules.nextStatusOptions
    .filter(option => {
      if (!option.condition) return true;
      
      // Simple condition evaluation (in production, use a proper expression evaluator)
      if (option.condition.includes('new_stock === true')) {
        return fieldValues?.new_stock === true;
      }
      if (option.condition.includes('product_added === true')) {
        return fieldValues?.product_added === true;
      }
      if (option.condition.includes('ir field is filled')) {
        return fieldValues?.ir && fieldValues.ir.trim() !== '';
      }
      if (option.condition.includes('inv field is filled')) {
        return fieldValues?.inv && fieldValues.inv.trim() !== '';
      }
      if (option.condition.includes('claim, cn, and credit_amount fields filled')) {
        return fieldValues?.claim && fieldValues?.cn && fieldValues?.credit_amount;
      }
      if (option.condition.includes('classification === "deliver_direct"')) {
        return classification === InvoiceClassification.DELIVER_DIRECT;
      }
      if (option.condition.includes('classification === "head_office"')) {
        return classification === InvoiceClassification.HEAD_OFFICE;
      }
      if (option.condition.includes('classification === "expense"')) {
        return classification === InvoiceClassification.EXPENSE;
      }
      if (option.condition.includes('classification === "credit_note"')) {
        return classification === InvoiceClassification.CREDIT_NOTE;
      }

      return true;
    })
    .map(option => option.status);
}

export function validateStatusTransition(
  fromStatus: InvoiceStatus,
  toStatus: InvoiceStatus,
  classification: InvoiceClassification
): boolean {
  const validTransitions = getNextStatusOptions(fromStatus, classification);
  return validTransitions.includes(toStatus);
}
